// src/lib/supabaseClient.ts - Complete with Card Shop Implementation
import { createClient } from '@supabase/supabase-js';
import { Card, Game, GameMove, PlayerCard, ActiveEffect, TSOProfile } from '../types';
import { PlayerDeck, DeckChoice } from '../types/index';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL!;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY!;

export const supabase = createClient(supabaseUrl, supabaseAnonKey);

// Digital Duel API functions with fixed defense logic and card shop
export const ddApi = {
  // Cards
  async getAllCards(): Promise<Card[]> {
    const { data, error } = await supabase
      .from('dd_cards')
      .select('*')
      .eq('active', true)
      .order('name');
    
    if (error) throw error;
    return data || [];
  },

  async getPlayerCards(userId: string): Promise<PlayerCard[]> {
    const { data, error } = await supabase
      .from('dd_player_cards')
      .select(`
        *,
        card:dd_cards(*)
      `)
      .eq('user_id', userId);
    
    if (error) throw error;
    return data || [];
  },

async getStarterPack(userId: string): Promise<{
  success: boolean;
  error?: string;
  cardsAdded?: number;
  cardNames?: string[];
  decksCreated?: number;
  message?: string;
}> {
  try {
    // Get the current session to ensure we have a fresh token
    const { data: sessionData } = await supabase.auth.getSession();
    
    if (!sessionData.session?.access_token) {
      return { success: false, error: 'Authentication session missing. Please try logging out and back in.' };
    }

    // SECURE: Use edge function with explicit authentication header
    const { data, error: functionError } = await supabase.functions.invoke('get-starter-pack-dd', {
      headers: {
        Authorization: `Bearer ${sessionData.session.access_token}`
      },
      body: {} // No body needed for starter pack
    });

    if (functionError) {
      console.error('Starter pack edge function error:', functionError);
      
      // Try to get the actual error response body
      let errorMessage = 'Starter pack failed';
      
      if (functionError.context) {
        try {
          const response = functionError.context;
          const errorText = await response.text();
          
          try {
            const errorData = JSON.parse(errorText);
            errorMessage = errorData.error || errorText;
          } catch {
            errorMessage = errorText || 'Unknown error';
          }
        } catch (readError) {
          console.error('Failed to read error response:', readError);
          errorMessage = `HTTP ${functionError.context.status}: ${functionError.message}`;
        }
      }
      
      return { success: false, error: errorMessage };
    }

    if (!data?.success) {
      return { success: false, error: data?.error || 'Starter pack failed' };
    }

    // Success! Return the results from the edge function
    return {
      success: true,
      cardsAdded: data.cardsAdded,
      cardNames: data.cardNames,
      decksCreated: data.decksCreated,
      message: data.message
    };

  } catch (error) {
    console.error('Network error in getStarterPack:', error);
    return { success: false, error: 'Network error - please try again' };
  }
},

// Add this function to check if player needs starter pack
needsStarterPack(playerCards: Card[]): boolean {
  const baseStarterCards = [1, 2, 6, 10, 14, 15, 16, 42];
  const ownedBaseCards = playerCards.filter(card => 
    baseStarterCards.includes(card.id)
  );
  // Show button if missing any of the 8 base cards
  return ownedBaseCards.length < baseStarterCards.length;
},
  
  // Leaderboard
// Leaderboard
async getLeaderboardData(): Promise<{
  players: Array<{
    username: string;
    faction_id: number;
    games_played: number;
    games_won: number;
  }>;
  factionStats: {
    defender_wins: number;
    attacker_wins: number;
  };
}> {
  // FAIREST APPROACH: Only count completed games
  const { data: games, error } = await supabase
    .from('dd_games')
    .select('winner_id, player1_id, player2_id, game_id')
    .eq('game_status', 'completed')  // <-- ONLY completed games
    .not('winner_id', 'is', null)
    .order('game_id');

  if (error) throw error;

  // Get all unique player IDs
  const playerIds = new Set<string>();
  games?.forEach(game => {
    playerIds.add(game.player1_id);
    playerIds.add(game.player2_id);
  });

  // Get player profiles
  const { data: profiles, error: profileError } = await supabase
    .from('profiles')
    .select('user_id, username, faction_id')
    .in('user_id', Array.from(playerIds));

  if (profileError) throw profileError;

  // Create a lookup map for profiles
  const profileMap = new Map();
  profiles?.forEach(profile => {
    profileMap.set(profile.user_id, profile);
  });

  // Process the data to create leaderboard
  const playerStats = new Map();
  let defenderWins = 0;
  let attackerWins = 0;

  games?.forEach(game => {
    const player1Profile = profileMap.get(game.player1_id);
    const player2Profile = profileMap.get(game.player2_id);
    
    // Initialize player stats
    if (player1Profile && !playerStats.has(player1Profile.user_id)) {
      playerStats.set(player1Profile.user_id, {
        username: player1Profile.username,
        faction_id: player1Profile.faction_id,
        games_played: 0,
        games_won: 0
      });
    }
    
    if (player2Profile && !playerStats.has(player2Profile.user_id)) {
      playerStats.set(player2Profile.user_id, {
        username: player2Profile.username,
        faction_id: player2Profile.faction_id,
        games_played: 0,
        games_won: 0
      });
    }
    
    // Count ONLY completed games as played
    if (player1Profile) {
      playerStats.get(player1Profile.user_id).games_played++;
    }
    if (player2Profile) {
      playerStats.get(player2Profile.user_id).games_played++;
    }
    
    // Count wins and faction wins
    if (game.winner_id === player1Profile?.user_id && player1Profile) {
      playerStats.get(player1Profile.user_id).games_won++;
      if (player1Profile.faction_id === 1) defenderWins++;
      else attackerWins++;
    } else if (game.winner_id === player2Profile?.user_id && player2Profile) {
      playerStats.get(player2Profile.user_id).games_won++;
      if (player2Profile.faction_id === 1) defenderWins++;
      else attackerWins++;
    }
  });

  // Convert to array and sort by wins (descending), then by win rate for ties
  const players = Array.from(playerStats.values())
    .filter(player => player.games_played > 0)
    .sort((a, b) => {
      // Primary sort: Most wins
      if (b.games_won !== a.games_won) {
        return b.games_won - a.games_won;
      }
      // Secondary sort: Better win rate
      const aWinRate = a.games_played > 0 ? a.games_won / a.games_played : 0;
      const bWinRate = b.games_played > 0 ? b.games_won / b.games_played : 0;
      return bWinRate - aWinRate;
    });

  return {
    players,
    factionStats: {
      defender_wins: defenderWins,
      attacker_wins: attackerWins
    }
  };
},
  
  // Add this to your ddApi object
async abandonGame(gameId: number, playerId: string): Promise<{ success: boolean; error?: string }> {
  try {
    // Verify player is involved in the game
    const { data: game } = await supabase
      .from('dd_games')
      .select('player1_id, player2_id, game_status')
      .eq('game_id', gameId)
      .single();

    if (!game || (game.player1_id !== playerId && game.player2_id !== playerId)) {
      return { success: false, error: 'Not authorized' };
    }

    if (game.game_status === 'completed') {
      return { success: false, error: 'Cannot abandon completed games' };
    }

    // Mark as abandoned
    const { error } = await supabase
      .from('dd_games')
      .update({ 
        game_status: 'abandoned',
        last_activity: new Date().toISOString()
      })
      .eq('game_id', gameId);

    if (error) throw error;
    return { success: true };

  } catch (error) {
    return { success: false, error: 'Server error' };
  }
},

// ADD THIS NEW FUNCTION after getPlayerCards() function
async getGameCards(userId: string, deckChoice?: DeckChoice): Promise<Card[]> {
  if (!deckChoice) {
    // Fallback to all owned cards for existing games without deck choice
    const playerCards = await this.getPlayerCards(userId);
    return playerCards.map(pc => pc.card!);
  }

  // At this point, deckChoice should NEVER be "random" because it was resolved at game creation
  if (deckChoice === 'random') {
    console.error('🚨 BUG: Found unresolved random deck choice in active game!');
    throw new Error('Invalid deck selection - please contact support');
  }

  // Extract deck number from choice (deck_1 -> 1, deck_2 -> 2, deck_3 -> 3)
  const deckSlot = parseInt(deckChoice.split('_')[1]) as 1 | 2 | 3;

  const { data: deck } = await supabase
    .from('dd_player_decks')
    .select('deck_slot, card_ids')
    .eq('user_id', userId)
    .eq('deck_slot', deckSlot)
    .single();

  if (!deck || !deck.card_ids || deck.card_ids.length === 0) {
    throw new Error(`Deck ${deckSlot} is empty. Please build your deck first.`);
  }

  // Get the specific cards from the selected deck
  const { data: cards } = await supabase
    .from('dd_cards')
    .select('*')
    .in('id', deck.card_ids)
    .eq('active', true);

  return cards || [];
},


  // CARD SHOP FUNCTIONS - SECURE IMPLEMENTATION

async purchaseCard(userId: string, cardId: number): Promise<{
  success: boolean;
  error?: string;
  newBalance?: number;
  cardName?: string;
  cardPrice?: number;
}> {
  try {
    // Get the current session to ensure we have a fresh token
    const { data: sessionData } = await supabase.auth.getSession();
    
    if (!sessionData.session?.access_token) {
      return { success: false, error: 'Authentication session missing. Please try logging out and back in.' };
    }

    // SECURE: Use edge function with explicit authentication header
    const { data, error: functionError } = await supabase.functions.invoke('purchase-card-dd', {
      headers: {
        Authorization: `Bearer ${sessionData.session.access_token}`
      },
      body: { cardId }
    });

    if (functionError) {
      console.error('Edge function error:', functionError);
      console.error('Error context:', functionError.context);
      
      // Try to get the actual error response body
      let errorMessage = 'Purchase failed';
      
      if (functionError.context) {
        try {
          // Clone the response to read it
          const response = functionError.context;
          const errorText = await response.text();
          console.error('Error response body:', errorText);
          
          // Try to parse as JSON
          try {
            const errorData = JSON.parse(errorText);
            errorMessage = errorData.error || errorText;
          } catch {
            // If not JSON, use the raw text
            errorMessage = errorText || 'Unknown error';
          }
        } catch (readError) {
          console.error('Failed to read error response:', readError);
          errorMessage = `HTTP ${functionError.context.status}: ${functionError.message}`;
        }
      }
      
      return { success: false, error: errorMessage };
    }

    if (!data?.success) {
      return { success: false, error: data?.error || 'Purchase failed' };
    }

    // Success! Return the results from the edge function
    return {
      success: true,
      newBalance: data.newBalance,
      cardName: data.cardName,
      cardPrice: data.cardPrice
    };

  } catch (error) {
    console.error('Network error in purchaseCard:', error);
    return { success: false, error: 'Network error - please try again' };
  }
},

  // Get all cards with prices for shop display
  async getShopCards(): Promise<Card[]> {
    const { data, error } = await supabase
      .from('dd_cards')
      .select('*')
      .eq('active', true)
      .order('price', { ascending: false }); // Show expensive cards first
    
    if (error) throw error;
    return data || [];
  },

  // Get cards player can afford
  async getAffordableCards(userId: string, maxPrice: number): Promise<Card[]> {
    const { data, error } = await supabase
      .from('dd_cards')
      .select('*')
      .eq('active', true)
      .lte('price', maxPrice)
      .order('price', { ascending: false });
    
    if (error) throw error;
    return data || [];
  },

  // Games - Enhanced with defense filtering
  async getPlayerGames(userId: string): Promise<Game[]> {
    const { data: games, error } = await supabase
      .from('dd_games')
      .select('*')
      .or(`player1_id.eq.${userId},player2_id.eq.${userId}`)
      .order('last_activity', { ascending: false });
    
    if (error) throw error;
    if (!games || games.length === 0) return [];

    // Get all unique player IDs
    const playerIds = new Set<string>();
    games.forEach(game => {
      playerIds.add(game.player1_id);
      playerIds.add(game.player2_id);
    });

    // Fetch usernames for all players
    const { data: profiles, error: profileError } = await supabase
      .from('profiles')
      .select('user_id, username, faction_id')
      .in('user_id', Array.from(playerIds));

    if (profileError) throw profileError;

    // Create a lookup map
    const profileMap = new Map();
    profiles?.forEach(profile => {
      profileMap.set(profile.user_id, profile);
    });

    // Enhance games with usernames - HIDE OPPONENT DEFENSE
    return games.map(game => {
      const isPlayer1 = game.player1_id === userId;
      return {
        ...game,
        player1_username: profileMap.get(game.player1_id)?.username || 'Unknown',
        player2_username: profileMap.get(game.player2_id)?.username || 'Unknown',
        player1_faction: profileMap.get(game.player1_id)?.faction_id,
        player2_faction: profileMap.get(game.player2_id)?.faction_id,
        // SECURITY: Hide opponent's defense value
        player1_defense: isPlayer1 ? game.player1_defense : undefined,
        player2_defense: isPlayer1 ? undefined : game.player2_defense,
      };
    });
  },

 // REPLACE your existing createGame function with this:
async createGame(player1Id: string, player2Id: string, player1DeckChoice?: DeckChoice): Promise<Game> {
  // Resolve random deck choice before storing
  let resolvedDeckChoice = player1DeckChoice;
  
  if (player1DeckChoice === 'random') {
    // Get player's decks to find which ones have cards
    const { data: allDecks } = await supabase
      .from('dd_player_decks')
      .select('deck_slot, card_ids')
      .eq('user_id', player1Id);

    // Find decks with cards
    const nonEmptyDecks = allDecks?.filter(deck => 
      deck.card_ids && deck.card_ids.length > 0
    ) || [];

    if (nonEmptyDecks.length === 0) {
      throw new Error('You must have at least one deck with cards to play');
    }

    // Randomly select from non-empty decks
    const randomDeck = nonEmptyDecks[Math.floor(Math.random() * nonEmptyDecks.length)];
    resolvedDeckChoice = `deck_${randomDeck.deck_slot}` as DeckChoice;
    
    console.log(`🎲 Random deck resolved: ${player1DeckChoice} → ${resolvedDeckChoice}`);
  }

  const gameData: any = {
    player1_id: player1Id,
    player2_id: player2Id,
    game_status: 'pending',
    player1_defense: 0,
    player2_defense: 0
  };

  // Store the RESOLVED deck choice (never store "random")
  if (resolvedDeckChoice) {
    gameData.player1_deck_choice = resolvedDeckChoice;
  }

  const { data, error } = await supabase
    .from('dd_games')
    .insert(gameData)
    .select()
    .single();
  
  if (error) throw error;
  return data;
},

async acceptGameInvite(gameId: number, player2DeckChoice?: DeckChoice): Promise<void> {
  // Resolve random deck choice before storing
  let resolvedDeckChoice = player2DeckChoice;
  
  if (player2DeckChoice === 'random') {
    // Get the current user (player2) ID from auth
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    // Get player's decks to find which ones have cards
    const { data: allDecks } = await supabase
      .from('dd_player_decks')
      .select('deck_slot, card_ids')
      .eq('user_id', user.id);

    // Find decks with cards
    const nonEmptyDecks = allDecks?.filter(deck => 
      deck.card_ids && deck.card_ids.length > 0
    ) || [];

    if (nonEmptyDecks.length === 0) {
      throw new Error('You must have at least one deck with cards to play');
    }

    // Randomly select from non-empty decks
    const randomDeck = nonEmptyDecks[Math.floor(Math.random() * nonEmptyDecks.length)];
    resolvedDeckChoice = `deck_${randomDeck.deck_slot}` as DeckChoice;
    
    console.log(`🎲 Random deck resolved: ${player2DeckChoice} → ${resolvedDeckChoice}`);
  }

  const updateData: any = {
    game_status: 'active',
    started_at: new Date().toISOString()
  };

  // Store the RESOLVED deck choice (never store "random")
  if (resolvedDeckChoice) {
    updateData.player2_deck_choice = resolvedDeckChoice;
  }

  const { error } = await supabase
    .from('dd_games')
    .update(updateData)
    .eq('game_id', gameId);
  
  if (error) throw error;
},

async getGame(gameId: number, currentUserId?: string): Promise<Game | null> {
  const { data, error } = await supabase
    .from('dd_games')
    .select('*')
    .eq('game_id', gameId)
    .single();
  
  if (error) return null;
  
  // SECURITY: Filter defense values based on current user AND active effects
  if (currentUserId && data) {
    const isPlayer1 = data.player1_id === currentUserId;
    
    // NEW: Check for reveal_opponent_defense effects
    const { data: revealEffects } = await supabase
      .from('dd_active_effects')
      .select('*')
      .eq('game_id', gameId)
      .eq('player_id', currentUserId)
      .eq('effect_type', 'reveal_opponent_defense');
    
    const hasRevealEffect = revealEffects && revealEffects.length > 0;
    
    return {
      ...data,
      // Show current player's defense (always visible)
      player1_defense: isPlayer1 ? data.player1_defense : (hasRevealEffect ? data.player1_defense : undefined),
      player2_defense: isPlayer1 ? (hasRevealEffect ? data.player2_defense : undefined) : data.player2_defense,
      // NEW: Add flag to indicate if opponent defense is revealed
      opponentDefenseRevealed: hasRevealEffect
    };
  }
  
  return data;
},
  // Update game state
 // SECURE: Update game state with validation
async updateGame(gameId: number, updates: Partial<Game>): Promise<void> {
  // Validate that updates only contain allowed fields and reasonable values
  const allowedFields = [
    'player1_health', 'player2_health', 'player1_defense', 'player2_defense',
    'current_turn', 'turn_number', 'last_activity', 'game_status', 'winner_id'
  ];
  
  // Remove any unauthorized fields
  const sanitizedUpdates: any = {};
  for (const [key, value] of Object.entries(updates)) {
    if (allowedFields.includes(key)) {
      sanitizedUpdates[key] = value;
    }
  }
  
  // Validate health values (0-20)
  if (sanitizedUpdates.player1_health !== undefined) {
    if (sanitizedUpdates.player1_health < 0 || sanitizedUpdates.player1_health > 20) {
      throw new Error('Invalid player1_health value');
    }
  }
  if (sanitizedUpdates.player2_health !== undefined) {
    if (sanitizedUpdates.player2_health < 0 || sanitizedUpdates.player2_health > 20) {
      throw new Error('Invalid player2_health value');
    }
  }
  
  // Validate defense values (>= 0, reasonable max of 50)
  if (sanitizedUpdates.player1_defense !== undefined) {
    if (sanitizedUpdates.player1_defense < 0 || sanitizedUpdates.player1_defense > 50) {
      throw new Error('Invalid player1_defense value');
    }
  }
  if (sanitizedUpdates.player2_defense !== undefined) {
    if (sanitizedUpdates.player2_defense < 0 || sanitizedUpdates.player2_defense > 50) {
      throw new Error('Invalid player2_defense value');
    }
  }
  
  // Validate turn values
  if (sanitizedUpdates.current_turn !== undefined) {
    if (sanitizedUpdates.current_turn !== 1 && sanitizedUpdates.current_turn !== 2) {
      throw new Error('Invalid current_turn value');
    }
  }
  
  const { error } = await supabase
    .from('dd_games')
    .update(sanitizedUpdates)
    .eq('game_id', gameId);
  
  if (error) throw error;
},

  // SECURE CARD PLAYING - Now with proper defense logic
 // Update your ddApi.playCard() function in supabaseClient.ts
// Replace the existing playCard function with this secure version:

async playCard(gameId: number, playerId: string, cardId: number): Promise<{
  success: boolean;
  error?: string;
  gameMove?: GameMove;
  gameUpdates?: any;
}> {
  try {
    // 1. Get full game state (unfiltered for calculations)
    const { data: game, error: gameError } = await supabase
      .from('dd_games')
      .select('*')
      .eq('game_id', gameId)
      .single();

    if (gameError || !game) return { success: false, error: 'Game not found' };
    
    if (game.game_status !== 'active') {
      return { success: false, error: 'Game is not active' };
    }

    // 2. Verify it's player's turn (client-side check for UX)
    const isPlayer1 = game.player1_id === playerId;
    const isPlayerTurn = (game.current_turn === 1 && isPlayer1) || 
                        (game.current_turn === 2 && !isPlayer1);
    
    if (!isPlayerTurn) {
      return { success: false, error: 'Not your turn' };
    }

    // 3. Get card details from database
    const { data: card, error: cardError } = await supabase
      .from('dd_cards')
      .select('*')
      .eq('id', cardId)
      .single();

    if (cardError || !card) {
      return { success: false, error: 'Card not found' };
    }

    // 4. Get current values
    const playerHealth = isPlayer1 ? game.player1_health : game.player2_health;
    const opponentHealth = isPlayer1 ? game.player2_health : game.player1_health;
    const playerDefense = isPlayer1 ? game.player1_defense : game.player2_defense;
    const opponentDefense = isPlayer1 ? game.player2_defense : game.player1_defense;

    // 5. Check play restrictions (client-side for UX)
    const canPlay = await this.validateCardPlay(gameId, playerId, card, playerHealth, opponentHealth);
    if (!canPlay.valid) {
      return { success: false, error: canPlay.reason };
    }

    // 6. Get active effects
    const { data: activeEffects } = await supabase
      .from('dd_active_effects')
      .select('*')
      .eq('game_id', gameId);

    // 7. Calculate effects CLIENT-SIDE (your existing logic)
    const effects = await this.calculateCardEffectsWithDefense(
      gameId, playerId, card, playerHealth, opponentHealth, 
      playerDefense, opponentDefense, isPlayer1, game.player1_id, game.player2_id
    );

    // 8. NEW: Send to validation edge function instead of direct DB update
    const { data: sessionData } = await supabase.auth.getSession();
    
    if (!sessionData.session?.access_token) {
      return { success: false, error: 'Authentication session missing' };
    }

    const { data, error: functionError } = await supabase.functions.invoke('validate-card-play-dd', {
      headers: {
        Authorization: `Bearer ${sessionData.session.access_token}`
      },
      body: {
        gameId,
        cardId,
        proposedUpdates: {
          player1_health: effects.newPlayer1Health,
          player2_health: effects.newPlayer2Health,
          player1_defense: effects.newPlayer1Defense,
          player2_defense: effects.newPlayer2Defense,
          damageDealt: effects.damageDealt,
          defenseConsumed: effects.defenseConsumed,
          healingDone: effects.healingDone,
          winner_id: effects.winnerId,
          newActiveEffects: effects.activeEffects,
          markAsUsed: effects.markAsUsed
        }
      }
    });

    if (functionError) {
      console.error('Edge function error:', functionError);
      
      // Try to get the actual error message
      let errorMessage = 'Server validation failed';
      if (functionError.context) {
        try {
          const response = functionError.context;
          const errorText = await response.text();
          try {
            const errorData = JSON.parse(errorText);
            errorMessage = errorData.error || errorText;
          } catch {
            errorMessage = errorText || 'Unknown server error';
          }
        } catch {
          errorMessage = `Server error: ${functionError.message}`;
        }
      }
      
      return { success: false, error: errorMessage };
    }

    if (!data?.success) {
      return { success: false, error: data?.error || 'Validation failed' };
    }

    // 9. Success! Return the validated results
    return {
      success: true,
      gameMove: data.gameMove,
      gameUpdates: data.gameUpdates
    };

  } catch (error) {
    console.error('Error playing card:', error);
    return { success: false, error: 'Network error - please try again' };
  }
},

  // FIXED: Validate if card can be played
  async validateCardPlay(gameId: number, playerId: string, card: Card, playerHealth: number, opponentHealth: number): Promise<{
    valid: boolean;
    reason?: string;
  }> {
    // Check health-based restrictions
    if (card.name === 'Desperate Strike' && playerHealth > 8) {
      return { valid: false, reason: 'Can only play when you have 8 or less health' };
    }

// NEW: Turn-based restriction for Security Audit
if (card.name === 'Security Audit') {
  const { data: game } = await supabase
    .from('dd_games')
    .select('turn_number')
    .eq('game_id', gameId)
    .single();
  
  if (game && game.turn_number <= 5) {
    return { valid: false, reason: 'Can only be played after turn 5' };
  }
}

if (card.name === 'AI Sentinel') {
  const { data: game } = await supabase
    .from('dd_games')
    .select('turn_number')
    .eq('game_id', gameId)
    .single();
  
  if (game && game.turn_number < 12) {
    return { valid: false, reason: 'Can only be played after turn 12' };
  }
  
  // Check if AI Sentinel already used in this game
  const { data: usedCard } = await supabase
    .from('dd_active_effects')
    .select('*')
    .eq('game_id', gameId)
    .eq('effect_type', 'ai_sentinel_used_in_game')
    .single();

  if (usedCard) {
    return { valid: false, reason: 'AI Sentinel has already been used in this game' };
  }
}

if (card.name === 'Infrastructure Attack' && opponentHealth < 10) {
  return { valid: false, reason: 'Can only play when opponent has 10+ health' };
}

// Check other once-per-match restrictions (per player) - AI Sentinel removed from list
if (card.name === 'Zero Day' || card.name === 'Logic Bomb' || card.name === 'The Administrator' || card.name === 'Multifactor Authentication' || card.name === 'Supply Chain Attack' || card.name === 'Memory Corruption' || card.name === 'Disaster Recovery' || card.name === 'Security Audit' || card.name === 'SAAT Training' || card.name === 'Network Firewall' || card.name === 'Red Team Exercise' || card.name === 'Zero Trust Protocol' || card.name === 'Progressive Ransomware' || card.name === 'Sleeper Agent') {

  const { data: usedCard } = await supabase
    .from('dd_active_effects')
    .select('*')
    .eq('game_id', gameId)
    .eq('player_id', playerId)
    .eq('effect_type', 'card_used_once')
.eq('effect_data->>card_id', card.id.toString())
    .single();

  if (usedCard) {
    return { valid: false, reason: `${card.name} can only be used once per match` };
  }
}

    return { valid: true };
  },

  // NEW: Calculate effects with proper defense system
 // FINAL WORKING VERSION: Complete fixed calculateCardEffectsWithDefense function
async calculateCardEffectsWithDefense(
  gameId: number, 
  playerId: string, 
  card: Card, 
  playerHealth: number, 
  opponentHealth: number,
  playerDefense: number,
  opponentDefense: number,
  isPlayer1: boolean,
  player1Id: string,
  player2Id: string
): Promise<{
  damageDealt: number;
  defenseConsumed: number;
  healingDone: number;
  newPlayer1Health: number;
  newPlayer2Health: number;
  newPlayer1Defense: number;
  newPlayer2Defense: number;
  gameOver: boolean;
  winnerId?: string;
  activeEffects: any[];
  markAsUsed: boolean;
}> {
  let damageDealt = 0;
  let defenseConsumed = 0;
  let healingDone = 0;
  let newPlayerHealth = playerHealth;
  let newOpponentHealth = opponentHealth;
  let newPlayerDefense = playerDefense;
  let newOpponentDefense = opponentDefense;
  const activeEffects: any[] = [];
  let markAsUsed = false;

  // Get existing active effects for special abilities
  const existingEffects = await this.getActiveEffects(gameId);

  // HELPER FUNCTION: Apply blocking effects and return true if blocked
  const applyBlockingEffects = async (effects: any[], currentPlayerId: string): Promise<boolean> => {
    // Check for nullify attack effects first
    const nullifyEffects = effects.filter(effect => 
      effect.player_id !== currentPlayerId && effect.effect_type === 'nullify_next_attack'
    );

    if (nullifyEffects.length > 0) {
      for (const effect of nullifyEffects) {
        await this.removeActiveEffect(effect.id);
      }
      return true; // Blocked
    }

    // Check for air gap isolation effects
    const airGapEffects = effects.filter(effect => 
      effect.player_id !== currentPlayerId && effect.effect_type === 'air_gap_isolation'
    );

    if (airGapEffects.length > 0) {
      for (const effect of airGapEffects) {
        await this.removeActiveEffect(effect.id);
      }
      return true; // Blocked
    }

    // Check for active blocking effects
    const blockEffects = effects.filter(effect => 
      effect.player_id !== currentPlayerId && effect.effect_type === 'block_attack'
    );

    if (blockEffects.length > 0) {
      for (const effect of blockEffects) {
        if (effect.effect_data.uses_remaining && effect.effect_data.uses_remaining > 1) {
          await supabase
            .from('dd_active_effects')
            .update({ 
              effect_data: { 
                ...effect.effect_data, 
                uses_remaining: effect.effect_data.uses_remaining - 1 
              } 
            })
            .eq('id', effect.id);
        } else {
          await this.removeActiveEffect(effect.id);
        }
        return true; // Blocked
      }
    }

    return false; // Not blocked
  };

  // FIXED: Handle APT persistent damage at turn start (BEFORE card effects)
  const aptEffects = existingEffects.filter(effect => 
    effect.player_id === playerId && effect.effect_type === 'apt_persistent_damage'
  );

  for (const effect of aptEffects) {
    const turnsRemaining = effect.effect_data.turns_remaining || 0;
    const damagePerTurn = effect.effect_data.damage_per_turn || 0;
    
    if (turnsRemaining > 0) {
      // Deal persistent damage to opponent through defense system
      if (newOpponentDefense > 0) {
        if (damagePerTurn <= newOpponentDefense) {
          defenseConsumed += damagePerTurn;
          newOpponentDefense -= damagePerTurn;
        } else {
          defenseConsumed += newOpponentDefense;
          const remainingDamage = damagePerTurn - newOpponentDefense;
          newOpponentDefense = 0;
          damageDealt += remainingDamage;
          newOpponentHealth = Math.max(0, newOpponentHealth - remainingDamage);
        }
      } else {
        damageDealt += damagePerTurn;
        newOpponentHealth = Math.max(0, newOpponentHealth - damagePerTurn);
      }
      
      // Update turns remaining
      const newTurnsRemaining = turnsRemaining - 1;
      if (newTurnsRemaining > 0) {
        await supabase
          .from('dd_active_effects')
          .update({ 
            effect_data: { 
              ...effect.effect_data, 
              turns_remaining: newTurnsRemaining 
            } 
          })
          .eq('id', effect.id);
      } else {
        await this.removeActiveEffect(effect.id);
      }
    }
  }

  // FIXED: Handle BIOS Rootkit persistent damage at turn start (BEFORE card effects)
  const biosRootkitEffects = existingEffects.filter(effect => 
    effect.player_id === playerId && effect.effect_type === 'bios_rootkit_damage'
  );

  for (const effect of biosRootkitEffects) {
    const persistentDamage = 1; // Always 1 damage per turn
    
    // Apply damage through defense system
    if (newOpponentDefense > 0) {
      if (persistentDamage <= newOpponentDefense) {
        defenseConsumed += persistentDamage;
        newOpponentDefense -= persistentDamage;
      } else {
        defenseConsumed += newOpponentDefense;
        const remainingDamage = persistentDamage - newOpponentDefense;
        newOpponentDefense = 0;
        damageDealt += remainingDamage;
        newOpponentHealth = Math.max(0, newOpponentHealth - remainingDamage);
      }
    } else {
      damageDealt += persistentDamage;
      newOpponentHealth = Math.max(0, newOpponentHealth - persistentDamage);
    }
  }

  // Handle Sleeper Agent activation check
  const sleeperAgentEffects = existingEffects.filter(effect => 
    effect.player_id === playerId && effect.effect_type === 'sleeper_agent_dormant'
  );

  for (const effect of sleeperAgentEffects) {
    const turnsRemaining = effect.effect_data.turns_remaining || 0;
    
    if (turnsRemaining <= 1) {
      // Activate sleeper agent - it will double the NEXT card played
      activeEffects.push({
        effect_type: 'sleeper_agent_active',
        effect_data: { 
          duration: 'next_card',
          multiplier: 2
        }
      });
      
      // Remove dormant effect
      await this.removeActiveEffect(effect.id);
    } else {
      // Reduce countdown
      await supabase
        .from('dd_active_effects')
        .update({ 
          effect_data: { 
            ...effect.effect_data, 
            turns_remaining: turnsRemaining - 1 
          } 
        })
        .eq('id', effect.id);
    }
  }

  // FIXED: Handle Network Jitter activation - calculate damage to ADD to card effects
  let jitterDamage = 0;
  const networkJitterEffects = existingEffects.filter(effect => 
    effect.player_id === playerId && effect.effect_type === 'network_jitter_next_turn'
  );

  for (const effect of networkJitterEffects) {
    // Generate random damage 0-10 to ADD to whatever card is played
    jitterDamage = Math.floor(Math.random() * 11); // 0 to 10
    
    // Remove the jitter effect (it's consumed this turn)
    await this.removeActiveEffect(effect.id);
    break; // Only one jitter effect should be active
  }

  // FIXED: Initialize effectiveCard with proper order - Sleeper Agent FIRST
  let effectiveCard = card;

  // Check for active Sleeper Agent effects and double base values FIRST
  const sleeperAgentActive = existingEffects.filter(effect => 
    effect.player_id === playerId && effect.effect_type === 'sleeper_agent_active'
  );

  if (sleeperAgentActive.length > 0) {
    effectiveCard = {
      ...effectiveCard,
      attack_value: effectiveCard.attack_value * 2,
      defense_value: effectiveCard.defense_value * 2
    };
    
    // Remove the sleeper agent effect
    for (const effect of sleeperAgentActive) {
      await this.removeActiveEffect(effect.id);
    }
  }

  // FIXED: Memory corruption swaps values AFTER sleeper agent but before other effects  
  const memoryCorruptionEffects = existingEffects.filter(effect => 
    effect.player_id !== playerId && effect.effect_type === 'swap_next_card_values'
  );

  if (memoryCorruptionEffects.length > 0) {
    // Create a modified card with swapped values
    effectiveCard = {
      ...effectiveCard,
      attack_value: effectiveCard.defense_value,
      defense_value: effectiveCard.attack_value
    };
    
    // Remove the memory corruption effect
    for (const effect of memoryCorruptionEffects) {
      await this.removeActiveEffect(effect.id);
    }
  }

  // Get player profile for faction-based effects (AI Sentinel)
  const { data: profile } = await supabase
    .from('profiles')
    .select('faction_id')
    .eq('user_id', playerId)
    .single();

  // Calculate base damage and effects
  let baseDamage = effectiveCard.attack_value;

  // Apply conditional damage modifiers
  switch (card.name) {
    case 'Spear Phishing':
      // FIXED: +2 attack if opponent has 5+ health (was 10+)
      if (opponentHealth >= 5) {
        baseDamage += 2; // 3 -> 5 damage
      }
      break;
    case 'Database Corruption':
      // +2 damage if opponent has 15+ health
      if (opponentHealth >= 15) {
        baseDamage += 2; // 5 -> 7 damage
      }
      break;
  }

  // Handle special legendary card - AI Sentinel
  if (card.name === 'AI Sentinel') {
    // Mark as used for entire GAME (not just player)
    activeEffects.push({
      effect_type: 'ai_sentinel_used_in_game',
      effect_data: { card_id: card.id, card_name: card.name, used_by: playerId }
    });

    // Faction-based effects - LEGENDARY POWER
    if (profile?.faction_id === 2) { 
      // ATTACKER FACTION - Devastating offensive (pierces defense but respects blocks)
      const sentinelBlocked = await applyBlockingEffects(existingEffects, playerId);
      
      if (!sentinelBlocked) {
        // AI Sentinel damage bypasses defense but not blocks
        damageDealt = 8 + jitterDamage;
        newOpponentHealth = Math.max(0, opponentHealth - damageDealt);
      } else {
        // Only jitter damage if blocked
        damageDealt = jitterDamage;
        if (jitterDamage > 0) {
          newOpponentHealth = Math.max(0, opponentHealth - jitterDamage);
        }
      }
      
    } else { 
      // DEFENDER FACTION - Ultimate protection
      // Block next 2 attacks
      activeEffects.push({
        effect_type: 'block_attack',
        effect_data: { duration: 'next_attack', uses_remaining: 2 }
      });
      
      // Restore to full health (legendary healing)
      healingDone = 20 - playerHealth;
      newPlayerHealth = 20;
    }
  } else {
    // Handle regular card effects
    switch (card.card_type) {
      case 'offensive': {
        let attackDamage = baseDamage;

        // Check for ransomware effects on opponent attacks
        const ransomwareEffects = existingEffects.filter(effect => 
          effect.player_id !== playerId && effect.effect_type === 'ransomware_attack_reduction'
        );

        for (const effect of ransomwareEffects) {
          const reduction = effect.effect_data.reduction_amount || 0;
          attackDamage = Math.max(0, attackDamage - reduction);
          
          // If attack reduced to 0, remove ransomware effect
          if (attackDamage === 0) {
            await this.removeActiveEffect(effect.id);
            // Set damage to 0 and skip further processing for this attack
            damageDealt = jitterDamage; // Only jitter damage if any
            if (jitterDamage > 0) {
              newOpponentHealth = Math.max(0, opponentHealth - jitterDamage);
            }
            break;
          }
        }

        // Check for piercing damage (cannot be reduced by defense)
        const isPiercing = card.name === 'DDoS Attack' || card.name === 'Social Engineering' || card.name === 'Zero Day';
        
        // Handle special card effects that bypass normal damage calculation
        if (card.name === 'Reboot') {
          const rebootBlocked = await applyBlockingEffects(existingEffects, playerId);
          
          if (!rebootBlocked) {
            // Deal damage and remove ALL opponent defense
            damageDealt = baseDamage + jitterDamage;
            newOpponentHealth = Math.max(0, opponentHealth - damageDealt);
            
            // Special effect: Remove ALL opponent defense
            if (opponentDefense > 0) {
              defenseConsumed = opponentDefense;
              newOpponentDefense = 0;
            }
          } else {
            damageDealt = jitterDamage;
            if (jitterDamage > 0) {
              newOpponentHealth = Math.max(0, opponentHealth - jitterDamage);
            }
          }
        } else if (card.name === 'Privilege Escalation') {
          let privEscDamage = baseDamage;
          
          // Check if opponent has more defense than player
          if (opponentDefense > playerDefense) {
            privEscDamage += 2; // +2 damage bonus
          }
          
          const privEscBlocked = await applyBlockingEffects(existingEffects, playerId);
          
          if (!privEscBlocked) {
            // Apply normal defense system to this damage
            if (opponentDefense > 0) {
              if (privEscDamage <= opponentDefense) {
                defenseConsumed = privEscDamage;
                newOpponentDefense = opponentDefense - privEscDamage;
                damageDealt = jitterDamage;
              } else {
                defenseConsumed = opponentDefense;
                newOpponentDefense = 0;
                damageDealt = (privEscDamage - opponentDefense) + jitterDamage;
                newOpponentHealth = Math.max(0, opponentHealth - damageDealt);
              }
            } else {
              damageDealt = privEscDamage + jitterDamage;
              newOpponentHealth = Math.max(0, opponentHealth - damageDealt);
            }
          } else {
            damageDealt = jitterDamage;
            if (jitterDamage > 0) {
              newOpponentHealth = Math.max(0, opponentHealth - jitterDamage);
            }
          }
        } else if (card.name === 'Supply Chain Attack') {
          const supplyChainBlocked = await applyBlockingEffects(existingEffects, playerId);
          
          if (!supplyChainBlocked) {
            damageDealt = baseDamage + jitterDamage;
            newOpponentHealth = Math.max(0, opponentHealth - damageDealt);
            
            // Add effect to nullify opponent's next attack
            activeEffects.push({
              effect_type: 'nullify_next_attack',
              effect_data: { duration: 'next_attack' }
            });
          } else {
            damageDealt = jitterDamage;
            if (jitterDamage > 0) {
              newOpponentHealth = Math.max(0, opponentHealth - jitterDamage);
            }
          }
              
          // Mark as used (once per match)
          markAsUsed = true;
          activeEffects.push({
            effect_type: 'card_used_once',
            effect_data: { card_id: card.id, card_name: card.name }
          });
        } else if (card.name === 'Memory Corruption') {
          const memoryCorruptionBlocked = await applyBlockingEffects(existingEffects, playerId);
          
          if (!memoryCorruptionBlocked) {
            damageDealt = baseDamage + jitterDamage;
            newOpponentHealth = Math.max(0, opponentHealth - damageDealt);
            
            // Add effect to swap opponent's next card values
            activeEffects.push({
              effect_type: 'swap_next_card_values',
              effect_data: { duration: 'next_card' }
            });
          } else {
            damageDealt = jitterDamage;
            if (jitterDamage > 0) {
              newOpponentHealth = Math.max(0, opponentHealth - jitterDamage);
            }
          }
          
          // Mark as used (once per match)
          markAsUsed = true;
          activeEffects.push({
            effect_type: 'card_used_once',
            effect_data: { card_id: card.id, card_name: card.name }
          });
        } else if (card.name === 'Zero Day' || card.name === 'Logic Bomb') {
          // These are piercing but still respect blocks
          const piercingBlocked = await applyBlockingEffects(existingEffects, playerId);
          
          if (!piercingBlocked) {
            damageDealt = attackDamage + jitterDamage;
            newOpponentHealth = Math.max(0, opponentHealth - damageDealt);
          } else {
            damageDealt = jitterDamage;
            if (jitterDamage > 0) {
              newOpponentHealth = Math.max(0, opponentHealth - jitterDamage);
            }
          }
          
          // Mark as used (once per match)
          markAsUsed = true;
          activeEffects.push({
            effect_type: 'card_used_once',
            effect_data: { card_id: card.id, card_name: card.name }
          });
        } else if (card.name === 'Social Engineering') {
          // Check for SAAT Training immunity
          const saatImmunity = existingEffects.filter(effect => 
            effect.player_id !== playerId && effect.effect_type === 'social_engineering_immunity'
          );

          const socialEngBlocked = await applyBlockingEffects(existingEffects, playerId);
          
          if (!socialEngBlocked) {
            if (saatImmunity.length > 0) {
              // SAAT Training blocks Social Engineering - apply normal defense logic
              if (opponentDefense > 0) {
                if (attackDamage <= opponentDefense) {
                  defenseConsumed = attackDamage;
                  newOpponentDefense = opponentDefense - attackDamage;
                  damageDealt = jitterDamage;
                } else {
                  defenseConsumed = opponentDefense;
                  newOpponentDefense = 0;
                  damageDealt = (attackDamage - opponentDefense) + jitterDamage;
                  newOpponentHealth = Math.max(0, opponentHealth - damageDealt);
                }
              } else {
                damageDealt = attackDamage + jitterDamage;
                newOpponentHealth = Math.max(0, opponentHealth - damageDealt);
              }
            } else {
              // Normal Social Engineering - bypasses defense but not blocks
              damageDealt = attackDamage + jitterDamage;
              newOpponentHealth = Math.max(0, opponentHealth - damageDealt);
            }
          } else {
            damageDealt = jitterDamage;
            if (jitterDamage > 0) {
              newOpponentHealth = Math.max(0, opponentHealth - jitterDamage);
            }
          }
           
        } else if (card.name === 'Incident Commander') {
          // FIXED: Conditional damage - respects blocks AND defense always
          const shouldDeal10 = opponentDefense >= 5;
          const commanderDamage = shouldDeal10 ? 10 : 3;
          
          const commanderBlocked = await applyBlockingEffects(existingEffects, playerId);
          
          if (!commanderBlocked) {
            // Both 3 and 10 damage go through normal defense system
            if (opponentDefense > 0) {
              if (commanderDamage <= opponentDefense) {
                defenseConsumed = commanderDamage;
                newOpponentDefense = opponentDefense - commanderDamage;
                damageDealt = jitterDamage;
              } else {
                defenseConsumed = opponentDefense;
                newOpponentDefense = 0;
                damageDealt = (commanderDamage - opponentDefense) + jitterDamage;
                newOpponentHealth = Math.max(0, opponentHealth - damageDealt);
              }
            } else {
              damageDealt = commanderDamage + jitterDamage;
              newOpponentHealth = Math.max(0, opponentHealth - damageDealt);
            }
          } else {
            damageDealt = jitterDamage;
            if (jitterDamage > 0) {
              newOpponentHealth = Math.max(0, opponentHealth - jitterDamage);
            }
          }
          
        } else if (card.name === 'Red Team Exercise') {
          // Count opponent's cards played (not including current turn)
          const { data: opponentMoves } = await supabase
            .from('dd_game_moves')
            .select('id')
            .eq('game_id', gameId)
            .neq('player_id', playerId);
          
          const opponentCardsPlayed = Math.min(opponentMoves?.length || 0, 15);
          
          const redTeamBlocked = await applyBlockingEffects(existingEffects, playerId);
          
          if (!redTeamBlocked) {
            damageDealt = opponentCardsPlayed + jitterDamage;
            newOpponentHealth = Math.max(0, opponentHealth - damageDealt);
          } else {
            damageDealt = jitterDamage;
            if (jitterDamage > 0) {
              newOpponentHealth = Math.max(0, opponentHealth - jitterDamage);
            }
          }
          
        } else if (card.name === 'Insider Threat') {
          const insiderBlocked = await applyBlockingEffects(existingEffects, playerId);
          
          if (!insiderBlocked) {
            damageDealt = 7 + jitterDamage;
            newOpponentHealth = Math.max(0, opponentHealth - damageDealt);
          } else {
            damageDealt = jitterDamage;
            if (jitterDamage > 0) {
              newOpponentHealth = Math.max(0, opponentHealth - jitterDamage);
            }
          }
          // Self-damage always applies regardless of blocking
          newPlayerHealth = Math.max(0, playerHealth - 2);
        } else if (card.name === 'Progressive Ransomware') {
          const ransomwareBlocked = await applyBlockingEffects(existingEffects, playerId);
          
          if (!ransomwareBlocked) {
            damageDealt = baseDamage + jitterDamage;
            newOpponentHealth = Math.max(0, opponentHealth - damageDealt);
          } else {
            damageDealt = jitterDamage;
            if (jitterDamage > 0) {
              newOpponentHealth = Math.max(0, opponentHealth - jitterDamage);
            }
          }
          
          // FIXED: Add escalating ransomware effect (turns 5, 10, 15, etc.)
          const { data: currentGame } = await supabase
            .from('dd_games')
            .select('turn_number')
            .eq('game_id', gameId)
            .single();
          
          const currentTurn = currentGame?.turn_number || 1;
          const baseReduction = 1;
          const escalationBonus = currentTurn % 5 === 0 ? Math.floor(currentTurn / 5) : 0; // Only on turns 5, 10, 15
          const totalReduction = baseReduction + escalationBonus;
          
          activeEffects.push({
            effect_type: 'ransomware_attack_reduction',
            effect_data: { 
              reduction_amount: totalReduction,
              base_reduction: baseReduction,
              escalation_bonus: escalationBonus,
              applied_turn: currentTurn
            }
          });
          
          // Mark as used (once per match)
          markAsUsed = true;
          activeEffects.push({
            effect_type: 'card_used_once',
            effect_data: { card_id: card.id, card_name: card.name }
          });

        } else if (card.name === 'Smalsy') {
          const smalsyBlocked = await applyBlockingEffects(existingEffects, playerId);
          
          if (!smalsyBlocked) {
            let smalsyDamage = baseDamage;
            
            // FIXED: Apply normal damage with defense system FIRST
            if (opponentDefense > 0) {
              if (smalsyDamage <= opponentDefense) {
                defenseConsumed = smalsyDamage;
                newOpponentDefense = opponentDefense - smalsyDamage;
                damageDealt = jitterDamage;
              } else {
                defenseConsumed = opponentDefense;
                newOpponentDefense = 0;
                damageDealt = (smalsyDamage - opponentDefense) + jitterDamage;
                newOpponentHealth = Math.max(0, opponentHealth - damageDealt);
              }
            } else {
              damageDealt = smalsyDamage + jitterDamage;
              newOpponentHealth = Math.max(0, opponentHealth - damageDealt);
            }
            
            // THEN check if opponent STILL has 3+ defense to steal from
            if (newOpponentDefense >= 3) {
              const defenseStolen = Math.min(2, newOpponentDefense);
              newOpponentDefense -= defenseStolen;
              newPlayerDefense += defenseStolen;
            }
          } else {
            damageDealt = jitterDamage;
            if (jitterDamage > 0) {
              newOpponentHealth = Math.max(0, opponentHealth - jitterDamage);
            }
          }

        } else if (card.name === 'Expired Certificate') {
          let expiredCertDamage = baseDamage;
          
          // FIXED: Check if opponent has NO defense at card play time
          if (opponentDefense === 0) {
            const { data: currentGame } = await supabase
              .from('dd_games')
              .select('turn_number')
              .eq('game_id', gameId)
              .single();
            
            const bonusDamage = Math.ceil((currentGame?.turn_number || 1) / 2);
            expiredCertDamage += bonusDamage;
          }
          
          const expiredCertBlocked = await applyBlockingEffects(existingEffects, playerId);
          
          if (!expiredCertBlocked) {
            damageDealt = expiredCertDamage + jitterDamage;
            newOpponentHealth = Math.max(0, opponentHealth - damageDealt);
          } else {
            damageDealt = jitterDamage;
            if (jitterDamage > 0) {
              newOpponentHealth = Math.max(0, opponentHealth - jitterDamage);
            }
          }
          
        } else if (card.name === 'BIOS Rootkit') {
          const biosBlocked = await applyBlockingEffects(existingEffects, playerId);
          
          if (!biosBlocked) {
            damageDealt = baseDamage + jitterDamage;
            newOpponentHealth = Math.max(0, opponentHealth - damageDealt);
          } else {
            damageDealt = jitterDamage;
            if (jitterDamage > 0) {
              newOpponentHealth = Math.max(0, opponentHealth - jitterDamage);
            }
          }
          
          // Add persistent damage effect for rest of match
          activeEffects.push({
            effect_type: 'bios_rootkit_damage',
            effect_data: { 
              damage_per_turn: 1,
              duration: 'permanent'
            }
          });
          
        } else {
          // Regular offensive card logic
          const regularAttackBlocked = await applyBlockingEffects(existingEffects, playerId);
          
          if (!regularAttackBlocked) {
            // Check for damage reduction effects - ONLY NON-PIERCING
            if (!isPiercing) {
              const reductionEffects = existingEffects.filter(effect => 
                effect.player_id !== playerId && effect.effect_type === 'damage_reduction'
              );

              for (const effect of reductionEffects) {
                const reduction = effect.effect_data.amount || 0;
                attackDamage = Math.max(0, attackDamage - reduction);
                await this.removeActiveEffect(effect.id);
                
                // Intrusion Detection counter-attack
                if (effect.effect_data.counter_damage) {
                  newPlayerHealth = Math.max(0, newPlayerHealth - effect.effect_data.counter_damage);
                }
                break;
              }
            }

            // Check for honeypot counter-attacks - triggers on any attack attempt
            const honeypotEffects = existingEffects.filter(effect => 
              effect.player_id !== playerId && effect.effect_type === 'honeypot_counter'
            );

            if (honeypotEffects.length > 0 && (damageDealt > 0 || attackDamage > 0)) {
              // Honeypot triggers on any attack attempt, even if blocked
              for (const effect of honeypotEffects) {
                const counterDamage = effect.effect_data.counter_damage || 0;
                newPlayerHealth = Math.max(0, newPlayerHealth - counterDamage);
                await this.removeActiveEffect(effect.id);
              }
            }

            // Handle SOC counter-attacks (only for offensive cards)
            const socCounterEffects = existingEffects.filter(effect => 
              effect.player_id !== playerId && effect.effect_type === 'soc_counter_attack'
            );

            if (socCounterEffects.length > 0 && card.card_type === 'offensive') {
              for (const effect of socCounterEffects) {
                const counterDamage = effect.effect_data.counter_damage || 0;
                const usesRemaining = effect.effect_data.uses_remaining || 0;
                
                if (usesRemaining > 0) {
                  // Deal counter damage to attacking player
                  newPlayerHealth = Math.max(0, newPlayerHealth - counterDamage);
                  
                  // Reduce uses remaining
                  const newUsesRemaining = usesRemaining - 1;
                  if (newUsesRemaining > 0) {
                    await supabase
                      .from('dd_active_effects')
                      .update({ 
                        effect_data: { 
                          ...effect.effect_data, 
                          uses_remaining: newUsesRemaining 
                        } 
                      })
                      .eq('id', effect.id);
                  } else {
                    await this.removeActiveEffect(effect.id);
                  }
                }
              }
            }

            // Apply defense system if not piercing
            if (!isPiercing && attackDamage > 0) {
              // Defense absorbs damage first
              if (opponentDefense > 0) {
                if (attackDamage <= opponentDefense) {
                  defenseConsumed = attackDamage;
                  newOpponentDefense = opponentDefense - attackDamage;
                  damageDealt = jitterDamage; // Only jitter gets through
                } else {
                  defenseConsumed = opponentDefense;
                  newOpponentDefense = 0;
                  damageDealt = (attackDamage - opponentDefense) + jitterDamage;
                  newOpponentHealth = Math.max(0, opponentHealth - damageDealt);
                }
              } else {
                damageDealt = attackDamage + jitterDamage;
                newOpponentHealth = Math.max(0, opponentHealth - damageDealt);
              }
            } else if (isPiercing && attackDamage > 0) {
              // Piercing damage bypasses defense entirely but adds jitter
              damageDealt = attackDamage + jitterDamage;
              newOpponentHealth = Math.max(0, opponentHealth - damageDealt);
            }
          } else {
            // Attack was blocked, only jitter damage applies
            damageDealt = jitterDamage;
            if (jitterDamage > 0) {
              newOpponentHealth = Math.max(0, opponentHealth - jitterDamage);
            }
          }
        }
        break;
      }
      
      case 'defensive': {
        // Defensive cards add to persistent defense AND may have special effects
        newPlayerDefense = playerDefense + effectiveCard.defense_value;

        switch (card.name) {
          case 'Network Firewall':
            // Special effect: Block next attack completely
            activeEffects.push({
              effect_type: 'block_attack',
              effect_data: { duration: 'next_attack' }
            });
            // Mark as used (once per match)
            markAsUsed = true;
            activeEffects.push({
              effect_type: 'card_used_once',
              effect_data: { card_id: card.id, card_name: card.name }
            });
            break;
          case 'Encryption Shield':
            // Special effect: Reduce next attack by 3
            activeEffects.push({
              effect_type: 'damage_reduction',
              effect_data: { amount: 3, duration: 'next_attack' }
            });
            break;
          case 'Intrusion Detection':
            // Special effect: Counter-attack
            activeEffects.push({
              effect_type: 'damage_reduction',
              effect_data: { amount: 2, duration: 'next_attack', counter_damage: 2 }
            });
            // Immediate healing
            healingDone = 2;
            newPlayerHealth = Math.min(20, playerHealth + healingDone);
            break;
          case 'Multifactor Authentication':
            // Special effect: Block next attack completely
            activeEffects.push({
              effect_type: 'block_attack',
              effect_data: { duration: 'next_attack' }
            });
            // Mark as used (once per match)
            markAsUsed = true;
            activeEffects.push({
              effect_type: 'card_used_once',
              effect_data: { card_id: card.id, card_name: card.name }
            });
            break;
          case 'Air Gap Protocol':
            // Special effect: Complete isolation - block next attack regardless of amount
            activeEffects.push({
              effect_type: 'air_gap_isolation',
              effect_data: { duration: 'next_attack' }
            });
            break;
          case 'Honeypot Trap':
            // Special effect: Counter-attack any incoming attack
            activeEffects.push({
              effect_type: 'honeypot_counter',
              effect_data: { counter_damage: 3, duration: 'next_attack' }
            });
            break;
          case 'Security Audit':
            // Special effect: Permanently reveal opponent's defense
            activeEffects.push({
              effect_type: 'reveal_opponent_defense',
              effect_data: { duration: 'permanent' }
            });
            // Mark as used (once per match)
            markAsUsed = true;
            activeEffects.push({
              effect_type: 'card_used_once',
              effect_data: { card_id: card.id, card_name: card.name }
            });
            break;
          case 'SAAT Training':
            // Special effect: Immunity to Social Engineering bypass
            activeEffects.push({
              effect_type: 'social_engineering_immunity',
              effect_data: { duration: 'permanent' }
            });
            // Mark as used (once per match)
            markAsUsed = true;
            activeEffects.push({
              effect_type: 'card_used_once',
              effect_data: { card_id: card.id, card_name: card.name }
            });
            break;
          case 'Redundant Infrastructure':
            // Add permanent backup systems effect
            activeEffects.push({
              effect_type: 'redundant_infrastructure_backup',
              effect_data: { duration: 'permanent' }
            });
            break;
          case 'Security Operations Center':
            // Gain 5 defense
            newPlayerDefense = playerDefense + 5;
            
            // Add counter-attack effect for next 2 offensive cards
            activeEffects.push({
              effect_type: 'soc_counter_attack',
              effect_data: { 
                counter_damage: 2, 
                uses_remaining: 2,
                duration: 'offensive_cards_only'
              }
            });
            break;
        }
        break;
      }

      case 'utility': {
        switch (card.name) {
          case 'Backup Server':
            healingDone = 5;
            newPlayerHealth = Math.min(20, playerHealth + healingDone);
            // FIXED: Mark as used (once per match)
            markAsUsed = true;
            activeEffects.push({
              effect_type: 'card_used_once',
              effect_data: { card_id: card.id, card_name: card.name }
            });
            break;
          case 'Incident Response':
            if (playerHealth <= 10) {
              healingDone = 5;
              newPlayerHealth = Math.min(20, playerHealth + healingDone);
            }
            break;
          case 'Security Patch':
            // Permanent defense bonus
            newPlayerDefense = playerDefense + 1;
            activeEffects.push({
              effect_type: 'defense_bonus',
              effect_data: { amount: 1, duration: 'permanent' }
            });
            break;
          case 'The Administrator':
            // Patch Release - Player gets +10 health and +10 defense, opponent gets +10 defense
            const healthGain = Math.min(10, 20 - playerHealth); // Don't exceed 20 max
            healingDone = healthGain;
            newPlayerHealth = Math.min(20, playerHealth + 10);
            newPlayerDefense = playerDefense + 10;
            
            // Opponent gets defensive compensation only
            newOpponentDefense = opponentDefense + 10;
            
            // Mark as used (once per match)
            markAsUsed = true;
            activeEffects.push({
              effect_type: 'card_used_once',
              effect_data: { card_id: card.id, card_name: card.name }
            });
            break;
          case 'Home Lab Engineer':
            // Basic attack damage with jitter
            let homeLawDamage = baseDamage;
            
            const homeLabBlocked = await applyBlockingEffects(existingEffects, playerId);
            
            if (!homeLabBlocked) {
              if (opponentDefense > 0) {
                if (baseDamage <= opponentDefense) {
                  defenseConsumed = baseDamage;
                  newOpponentDefense = opponentDefense - baseDamage;
                  damageDealt = jitterDamage;
                } else {
                  defenseConsumed = opponentDefense;
                  newOpponentDefense = 0;
                  damageDealt = (homeLawDamage - opponentDefense) + jitterDamage;
                  newOpponentHealth = Math.max(0, opponentHealth - damageDealt);
                }
              } else {
                damageDealt = homeLawDamage + jitterDamage;
                newOpponentHealth = Math.max(0, opponentHealth - damageDealt);
              }
            } else {
              damageDealt = jitterDamage;
              if (jitterDamage > 0) {
                newOpponentHealth = Math.max(0, opponentHealth - jitterDamage);
              }
            }
            
            // FIXED: Redundant systems - count ALL defensive cards played this entire game
            const { data: gameHistoryData } = await supabase
              .from('dd_game_moves')
              .select('card_type')
              .eq('game_id', gameId)
              .eq('player_id', playerId)
              .eq('card_type', 'defensive');
            
            const defensiveCardsPlayed = gameHistoryData?.length || 0;
            newPlayerDefense = playerDefense + (defensiveCardsPlayed * 2);
            break;
          case 'Disaster Recovery':
            // FIXED: Emergency restoration - restore to exactly 15 health
            if (playerHealth <= 5) {
              healingDone = 15 - playerHealth;
              newPlayerHealth = 15;
            }
            
            // Mark as used (once per match)
            markAsUsed = true;
            activeEffects.push({
              effect_type: 'card_used_once',
              effect_data: { card_id: card.id, card_name: card.name }
            });
            break;
          case 'Zero Trust Protocol':
            // FIXED: Remove all player defense and deal that much damage (respects blocks AND defense)
            const defenseToRemove = playerDefense;
            newPlayerDefense = 0;
            
            const zeroTrustBlocked = await applyBlockingEffects(existingEffects, playerId);
            
            if (!zeroTrustBlocked) {
              // This damage goes through normal defense system
              if (opponentDefense > 0) {
                if (defenseToRemove <= opponentDefense) {
                  defenseConsumed = defenseToRemove;
                  newOpponentDefense = opponentDefense - defenseToRemove;
                  damageDealt = jitterDamage;
                } else {
                  defenseConsumed = opponentDefense;
                  newOpponentDefense = 0;
                  damageDealt = (defenseToRemove - opponentDefense) + jitterDamage;
                  newOpponentHealth = Math.max(0, opponentHealth - damageDealt);
                }
              } else {
                damageDealt = defenseToRemove + jitterDamage;
                newOpponentHealth = Math.max(0, opponentHealth - damageDealt);
              }
            } else {
              damageDealt = jitterDamage;
              if (jitterDamage > 0) {
                newOpponentHealth = Math.max(0, opponentHealth - jitterDamage);
              }
            }
            
            // Mark as used (once per match)
            markAsUsed = true;
            activeEffects.push({
              effect_type: 'card_used_once',
              effect_data: { card_id: card.id, card_name: card.name }
            });
            break;
          case 'CTRL+Z':
            // FIXED: Get the last opponent move against this player and restore both health AND defense
            const { data: lastOpponentMove } = await supabase
              .from('dd_game_moves')
              .select('damage_dealt, defense_consumed')
              .eq('game_id', gameId)
              .neq('player_id', playerId)
              .order('turn_number', { ascending: false })
              .limit(1)
              .single();

            if (lastOpponentMove) {
              // Restore health lost from last attack
              if (lastOpponentMove.damage_dealt > 0) {
                const healthToRestore = lastOpponentMove.damage_dealt;
                healingDone = healthToRestore;
                newPlayerHealth = Math.min(20, playerHealth + healthToRestore);
              }
              
              // Restore defense consumed from last attack
              if (lastOpponentMove.defense_consumed > 0) {
                const defenseToRestore = lastOpponentMove.defense_consumed;
                newPlayerDefense = playerDefense + defenseToRestore;
              }
            }
            
            // Mark as used (once per match)
            markAsUsed = true;
            activeEffects.push({
              effect_type: 'card_used_once',
              effect_data: { card_id: card.id, card_name: card.name }
            });
            break;
          case 'System Hardening':
            // Gain 3 defense
            newPlayerDefense = playerDefense + 3;
            
            // FIXED: Check if total defense >= 5 AFTER adding the 3 defense
            if (newPlayerDefense >= 5) {
              activeEffects.push({
                effect_type: 'defense_bonus',
                effect_data: { amount: 1, duration: 'permanent' }
              });
              newPlayerDefense += 1; // Apply the bonus immediately
            }
            break;
          case 'Advanced Persistent Threat':
            // FIXED: Deal 1 damage immediately through blocking and defense systems
            let aptDamage = 1;
            
            const aptBlocked = await applyBlockingEffects(existingEffects, playerId);
            
            if (!aptBlocked) {
              if (opponentDefense > 0) {
                if (1 <= opponentDefense) {
                  defenseConsumed = 1;
                  newOpponentDefense = opponentDefense - 1;
                  damageDealt = jitterDamage;
                } else {
                  defenseConsumed = opponentDefense;
                  newOpponentDefense = 0;
                  damageDealt = (aptDamage - opponentDefense) + jitterDamage;
                  newOpponentHealth = Math.max(0, opponentHealth - damageDealt);
                }
              } else {
                damageDealt = aptDamage + jitterDamage;
                newOpponentHealth = Math.max(0, opponentHealth - damageDealt);
              }
            } else {
              damageDealt = jitterDamage;
              if (jitterDamage > 0) {
                newOpponentHealth = Math.max(0, opponentHealth - jitterDamage);
              }
            }
            
            // Add persistent threat effect for next 3 turns
            activeEffects.push({
              effect_type: 'apt_persistent_damage',
              effect_data: { 
                damage_per_turn: 2, 
                turns_remaining: 3,
                duration: 'turn_based'
              }
            });
            
            // Mark as used (once per match)
            markAsUsed = true;
            activeEffects.push({
              effect_type: 'card_used_once',
              effect_data: { card_id: card.id, card_name: card.name }
            });
            break;
          case 'Network Jitter':
            // Set up jitter for next turn - this card just sets up the effect
            const { data: currentGame1 } = await supabase
              .from('dd_games')
              .select('turn_number')
              .eq('game_id', gameId)
              .single();
            
            activeEffects.push({
              effect_type: 'network_jitter_next_turn',
              effect_data: { 
                duration: 'next_turn',
                activation_turn: (currentGame1?.turn_number || 0) + 1
              }
            });
            break;
          case 'Sleeper Agent':
            // Generate random dormancy period (1-7 turns)
            const dormancyTurns = Math.floor(Math.random() * 7) + 1;
            const { data: currentGame2 } = await supabase
              .from('dd_games')
              .select('turn_number')
              .eq('game_id', gameId)
              .single();
            
            activeEffects.push({
              effect_type: 'sleeper_agent_dormant',
              effect_data: { 
                turns_remaining: dormancyTurns,
                activation_turn: (currentGame2?.turn_number || 0) + dormancyTurns
              }
            });
            
            // Mark as used (once per match)
            markAsUsed = true;
            activeEffects.push({
              effect_type: 'card_used_once',
              effect_data: { card_id: card.id, card_name: card.name }
            });
            break;
          case 'Zero Trust Verification':
            // Gain 2 defense first
            newPlayerDefense = playerDefense + 2;
            
            // FIXED: Reveal opponent defense, then check if they have more than you (before the +2)
            activeEffects.push({
              effect_type: 'reveal_opponent_defense',
              effect_data: { duration: 'this_turn_only' }
            });
            
            // If opponent has more defense than you HAD, steal half
            if (opponentDefense > playerDefense) {
              const defenseToSteal = Math.floor(opponentDefense / 2);
              if (defenseToSteal > 0) {
                newOpponentDefense = opponentDefense - defenseToSteal;
                newPlayerDefense = newPlayerDefense + defenseToSteal; // Add to the already increased defense
              }
            }
            break;
        }
        break;
      }
    }
  }

  // Check for redundant infrastructure backup effects AFTER all damage is calculated
  const redundantInfraEffects = existingEffects.filter(effect => 
    effect.player_id !== playerId && effect.effect_type === 'redundant_infrastructure_backup'
  );

  if (redundantInfraEffects.length > 0 && damageDealt >= 5) {
    // Backup systems activate - restore 2 health to the damaged player (opponent)
    healingDone += 2;
    newOpponentHealth = Math.min(20, newOpponentHealth + 2);
  }

  // Determine final values based on player position
  const finalPlayer1Health = isPlayer1 ? newPlayerHealth : newOpponentHealth;
  const finalPlayer2Health = isPlayer1 ? newOpponentHealth : newPlayerHealth;
  const finalPlayer1Defense = isPlayer1 ? newPlayerDefense : newOpponentDefense;
  const finalPlayer2Defense = isPlayer1 ? newOpponentDefense : newPlayerDefense;

  // Check for game over
  const gameOver = finalPlayer1Health <= 0 || finalPlayer2Health <= 0;
  const winnerId = gameOver ? (finalPlayer1Health > 0 ? player1Id : player2Id) : undefined;

  return {
    damageDealt,
    defenseConsumed,
    healingDone,
    newPlayer1Health: finalPlayer1Health,
    newPlayer2Health: finalPlayer2Health,
    newPlayer1Defense: finalPlayer1Defense,
    newPlayer2Defense: finalPlayer2Defense,
    gameOver,
    winnerId,
    activeEffects,
    markAsUsed
  };
},

  async getGameMoves(gameId: number): Promise<GameMove[]> {
    const { data, error } = await supabase
      .from('dd_game_moves')
      .select(`
        *,
        card:dd_cards(*)
      `)
      .eq('game_id', gameId)
      .order('timestamp');
    
    if (error) throw error;
    return data || [];
  },

  // Players
  async getAvailablePlayers(currentUserId: string): Promise<TSOProfile[]> {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .neq('user_id', currentUserId)
       .order('last_activity_date', { ascending: false })
	       .order('consecutive_days_played', { ascending: false });
    
    if (error) throw error;
    return data || [];
  },

  // Active Effects Management
  async getActiveEffects(gameId: number): Promise<ActiveEffect[]> {
    const { data, error } = await supabase
      .from('dd_active_effects')
      .select('*')
      .eq('game_id', gameId);
    
    if (error) throw error;
    return data || [];
  },

  async addActiveEffects(gameId: number, playerId: string, effects: any[]): Promise<void> {
    const effectsToInsert = effects.map(effect => ({
      game_id: gameId,
      player_id: playerId,
      effect_type: effect.effect_type,
      effect_data: effect.effect_data
    }));

    const { error } = await supabase
      .from('dd_active_effects')
      .insert(effectsToInsert);

    if (error) throw error;
  },

  async removeActiveEffect(effectId: number): Promise<void> {
  console.log(`🗑️ ATTEMPTING to remove effect ID: ${effectId}`);
  
  const { error } = await supabase
    .from('dd_active_effects')
    .delete()
    .eq('id', effectId);
    
  if (error) {
    console.error('❌ FAILED to remove effect:', error);
    throw error;
  }
  
  console.log(`✅ SUCCESSFULLY removed effect ID: ${effectId}`);
},

  async markCardAsUsed(gameId: number, playerId: string, cardId: number): Promise<void> {
    // This is handled in addActiveEffects, but kept for clarity
  }
};